import { useEffect, useRef } from 'react'
import { Canvas, FabricImage, Text } from 'fabric'
import { updateCanvasDimensions, renderCanvas } from '../utils/canvasUtils'
import { updateMediaOnResize } from '../utils/mediaUtils'
import { calculateTextPosition } from '../../utils/text/textPosition'
import { CANVAS } from '../../constants'
import type { MediaDimensions, CanvasScale } from '../types'
import type { TextAlignment, MarkingPosition } from '../../types'

/**
 * Параметры для хука изменения размеров canvas
 */
export interface UseCanvasResizeParams {
    canvasRef: React.RefObject<HTMLCanvasElement | null>
    fabricCanvasRef: React.MutableRefObject<Canvas | null>
    imageObjectRef: React.MutableRefObject<FabricImage | null>
    textObjectRef: React.MutableRefObject<Text | null>
    positionRef: React.MutableRefObject<MarkingPosition | null>
    mediaDimensionsRef: React.MutableRefObject<MediaDimensions | null>
    previousScaleRef: React.MutableRefObject<CanvasScale | null>
    width: number
    height: number
    textAlignment: TextAlignment
    safeArea: {
        top: number
        bottom: number
        left: number
        right: number
    }
    onScaleChange?: (scale: CanvasScale) => void
}

/**
 * Хук для обработки изменения размеров canvas
 * Обновляет размеры canvas и позиции объектов при изменении размера контейнера
 */
export function useCanvasResize(params: UseCanvasResizeParams): void {
    const {
        canvasRef,
        fabricCanvasRef,
        imageObjectRef,
        textObjectRef,
        positionRef,
        mediaDimensionsRef,
        previousScaleRef,
        width,
        height,
        textAlignment,
        safeArea,
        onScaleChange,
    } = params

    useEffect(() => {
        // Ждем, пока canvas будет инициализирован
        if (!fabricCanvasRef.current || !canvasRef.current) {
            return
        }

        const fabricCanvas = fabricCanvasRef.current
        const canvasElement = canvasRef.current

        // Проверяем, что canvas привязан к DOM
        if (!canvasElement.parentElement) {
            return
        }

        const containerElement = canvasElement.parentElement
        if (!containerElement) {
            return
        }

        // Функция для обновления размеров канваса и медиа
        const updateCanvasAndMedia = (): void => {
            if (!fabricCanvas || !canvasRef.current) {
                return
            }

            const containerElement = canvasRef.current.parentElement
            if (!containerElement) {
                return
            }

            const currentMediaDimensions = mediaDimensionsRef.current
            if (!currentMediaDimensions) {
                return
            }

            // Обновляем размеры канваса
            const { width: newCanvasWidth, height: newCanvasHeight } =
                updateCanvasDimensions({
                    canvas: fabricCanvas,
                    containerWidth: containerElement.clientWidth,
                    containerHeight: containerElement.clientHeight,
                    minWidth: width,
                    minHeight: height,
                    mediaDimensions: currentMediaDimensions,
                })

            // Обновляем масштаб и позицию медиа
            if (imageObjectRef.current) {
                updateMediaOnResize({
                    image: imageObjectRef.current,
                    canvasWidth: newCanvasWidth,
                    canvasHeight: newCanvasHeight,
                    originalWidth: currentMediaDimensions.width,
                    originalHeight: currentMediaDimensions.height,
                })

                renderCanvas({
                    canvas: fabricCanvas,
                    textObj: imageObjectRef.current,
                })

                // Уведомляем о масштабе при изменении размеров canvas
                if (onScaleChange) {
                    const newScale: CanvasScale = {
                        canvasWidth: newCanvasWidth,
                        canvasHeight: newCanvasHeight,
                        originalWidth: currentMediaDimensions.width,
                        originalHeight: currentMediaDimensions.height,
                    }
                    const prevScale = previousScaleRef.current
                    if (
                        !prevScale ||
                        prevScale.canvasWidth !== newScale.canvasWidth ||
                        prevScale.canvasHeight !== newScale.canvasHeight ||
                        prevScale.originalWidth !== newScale.originalWidth ||
                        prevScale.originalHeight !== newScale.originalHeight
                    ) {
                        previousScaleRef.current = newScale
                        onScaleChange(newScale)
                    }
                }
            }

            // Обновляем позицию текста, если он есть
            if (textObjectRef.current && positionRef.current) {
                const textObj = textObjectRef.current
                const textWidth = textObj.width * (textObj.scaleX ?? 1)
                const textHeight = textObj.height * (textObj.scaleY ?? 1)
                const textAlign =
                    (textObj.textAlign as TextAlignment) ?? textAlignment

                // Пересчитываем позицию с учетом выравнивания и размеров текста
                const textPosition = calculateTextPosition({
                    position: positionRef.current,
                    canvasWidth: newCanvasWidth,
                    canvasHeight: newCanvasHeight,
                    textAlign,
                    textWidth,
                    textHeight,
                    safeArea,
                })
                textObj.set({
                    left: textPosition.left,
                    top: textPosition.top,
                })
                renderCanvas({ canvas: fabricCanvas, textObj })
            } else if (textObjectRef.current) {
                // Если позиция не сохранена, центрируем текст
                const textObj = textObjectRef.current
                textObj.set({
                    left: newCanvasWidth / 2,
                    top: newCanvasHeight / 2,
                })
                renderCanvas({ canvas: fabricCanvas, textObj })
            }
        }

        // Обработчик изменения размера окна с debounce для оптимизации
        let resizeTimeout: NodeJS.Timeout | null = null
        const handleResize = (): void => {
            if (resizeTimeout) {
                clearTimeout(resizeTimeout)
            }
            resizeTimeout = setTimeout(() => {
                updateCanvasAndMedia()
            }, CANVAS.RESIZE_DEBOUNCE_TIMEOUT)
        }

        // Добавляем обработчик resize для окна
        window.addEventListener('resize', handleResize)

        // Используем ResizeObserver для отслеживания изменений размера контейнера
        let resizeObserver: ResizeObserver | null = null
        if (containerElement && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver((entries) => {
                // Проверяем, что размер действительно изменился
                for (const entry of entries) {
                    const { width: entryWidth, height: entryHeight } =
                        entry.contentRect
                    if (entryWidth > 0 && entryHeight > 0) {
                        handleResize()
                    }
                }
            })
            resizeObserver.observe(containerElement)
        }

        // Cleanup при размонтировании
        return () => {
            window.removeEventListener('resize', handleResize)
            if (resizeTimeout) {
                clearTimeout(resizeTimeout)
            }
            if (resizeObserver) {
                resizeObserver.disconnect()
            }
        }
    }, [
        canvasRef,
        fabricCanvasRef,
        imageObjectRef,
        textObjectRef,
        positionRef,
        mediaDimensionsRef,
        previousScaleRef,
        width,
        height,
        textAlignment,
        onScaleChange,
    ])
}
